import React, { useEffect, useState, useCallback } from 'react';
import { hos as hosApi, extractError } from '../services/api';
import { TableSkeleton } from '../components/ui/LoadingSpinner';
import { useToast } from '../store/ToastContext';

function fmtMin(mins) {
  if (mins == null) return '—';
  const m = Math.max(0, parseInt(mins));
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function hosColor(mins) {
  const m = parseInt(mins || 0);
  if (m <= 0)   return 'bg-gray-800 text-white';
  if (m < 60)   return 'bg-red-100 text-red-800 font-semibold';
  if (m < 180)  return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-50 text-green-800';
}

function CircleClock({ value, max, label, color }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  const circumference = 2 * Math.PI * 16;
  const clr = value < 60 ? '#ef4444' : value < 180 ? '#f59e0b' : color || '#10b981';

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-16 h-16">
        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
          <circle cx="18" cy="18" r="16" fill="none" stroke="#e5e7eb" strokeWidth="3" />
          <circle cx="18" cy="18" r="16" fill="none" stroke={clr} strokeWidth="3"
            strokeDasharray={`${(pct / 100) * circumference} ${circumference}`}
            strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold text-gray-900" style={{ fontSize: '9px' }}>
            {Math.floor(parseInt(value || 0) / 60)}h
          </span>
        </div>
      </div>
      <div className="text-xs font-semibold text-gray-800 mt-1">{fmtMin(value)}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function HOSLogGrid({ logs }) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const statusColors = {
    off_duty:             'bg-gray-300',
    sleeper_berth:        'bg-blue-400',
    driving:              'bg-green-500',
    on_duty_not_driving:  'bg-yellow-400',
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">24-Hour Log (FMCSA)</p>
      <div className="min-w-[600px]">
        <div className="flex mb-1">
          <div className="w-28 flex-shrink-0" />
          {hours.map(h => (
            <div key={h} className="flex-1 text-center text-xs text-gray-400 leading-none" style={{ fontSize: '10px' }}>{h}</div>
          ))}
        </div>
        {Object.entries(statusColors).map(([status, color]) => (
          <div key={status} className="flex items-center mb-0.5">
            <div className="w-28 flex-shrink-0 text-xs text-gray-600 capitalize pr-2" style={{ fontSize: '10px' }}>
              {status.replace(/_/g, ' ')}
            </div>
            <div className="flex-1 flex border border-gray-200 h-5 rounded overflow-hidden">
              {hours.map(h => {
                const active = logs.some(l =>
                  l.status === status &&
                  new Date(l.start_time).getHours() <= h &&
                  (!l.end_time || new Date(l.end_time).getHours() > h)
                );
                return <div key={h} className={`flex-1 ${active ? color : ''} border-r border-gray-100 last:border-0`} />;
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-4 mt-2">
        {Object.entries(statusColors).map(([s, c]) => (
          <div key={s} className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded-sm inline-block ${c}`} />
            <span className="text-xs text-gray-500 capitalize">{s.replace(/_/g, ' ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DriverHOSPanel({ summary, onClose }) {
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    hosApi.logs(summary.driver_id, new Date().toISOString().split('T')[0])
      .then(data => setLogs(Array.isArray(data) ? data : data?.data || []))
      .catch(() => toast.error('Failed to load logs'))
      .finally(() => setLoading(false));
  }, [summary.driver_id]);

  const violations = (() => {
    try { return typeof summary.violations === 'string' ? JSON.parse(summary.violations) : (summary.violations || []); }
    catch { return []; }
  })();

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-40 flex flex-col animate-slide-up overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-white flex-shrink-0">
        <div>
          <h3 className="font-bold text-gray-900">{summary.driver_name}</h3>
          <p className="text-xs text-gray-500">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* HOS Clocks */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Hours of Service</p>
          <div className="grid grid-cols-3 gap-4">
            <CircleClock value={summary.driving_minutes_remaining} max={660} label="Driving" color="#10b981" />
            <CircleClock value={summary.duty_window_minutes_remaining} max={840} label="Duty Window" color="#3b82f6" />
            <CircleClock value={summary.cycle_minutes_remaining} max={4200} label="Cycle" color="#8b5cf6" />
          </div>
        </div>

        {/* Details */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
          {[
            { label: 'Driving Used',       value: fmtMin(summary.driving_minutes_used) },
            { label: 'On-Duty Used',        value: fmtMin(summary.on_duty_minutes_used) },
            { label: 'Break Required In',   value: fmtMin(summary.minutes_until_break_required), alert: parseInt(summary.minutes_until_break_required) < 60 },
          ].map(r => (
            <div key={r.label} className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{r.label}</span>
              <span className={`text-sm font-semibold ${r.alert ? 'text-red-600' : 'text-gray-900'}`}>{r.value}</span>
            </div>
          ))}
        </div>

        {/* Violations */}
        {violations.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">Active Violations</p>
            {violations.map((v, i) => (
              <div key={i} className="flex gap-2 p-3 bg-red-50 border border-red-100 rounded-lg mb-2">
                <span className="text-red-500 text-base">⚠</span>
                <div>
                  <div className="text-xs font-bold text-red-700">{v.type?.replace(/_/g, ' ')}</div>
                  <div className="text-xs text-red-600">{v.description}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {summary.needs_34hr_restart && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl">
            <p className="text-sm font-semibold text-orange-700">34-Hour Restart Required</p>
            <p className="text-xs text-orange-600 mt-1">Driver has exhausted weekly cycle hours and must rest 34+ consecutive hours.</p>
          </div>
        )}

        {/* Log grid */}
        {loading ? (
          <div className="h-24 bg-gray-50 animate-pulse rounded-xl" />
        ) : (
          <HOSLogGrid logs={logs} />
        )}
      </div>

      <div className="flex-shrink-0 px-5 py-3 border-t border-gray-200 bg-gray-50 flex gap-2">
        <button className="btn-secondary flex-1 py-2 text-xs">Export CSV</button>
        <button className="btn-secondary flex-1 py-2 text-xs">Export PDF</button>
      </div>
    </div>
  );
}

export function HOSDashboard() {
  const toast = useToast();
  const [fleet, setFleet]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await hosApi.fleet();
      setFleet(Array.isArray(data) ? data : data?.data || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Count alerts
  const alerts = fleet.filter(d => {
    const viol = (() => { try { return typeof d.violations === 'string' ? JSON.parse(d.violations) : (d.violations || []); } catch { return []; } })();
    return viol.length > 0 || parseInt(d.driving_minutes_remaining) < 60 || d.needs_34hr_restart;
  });

  return (
    <div className="flex-1 flex overflow-hidden bg-gray-50">
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">HOS Compliance Dashboard</h1>
              <p className="text-sm text-gray-500">FMCSA 49 CFR Part 395 · {fleet.length} drivers tracked</p>
            </div>
            <div className="flex items-center gap-3">
              {alerts.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-red-700">{alerts.length} alert{alerts.length !== 1 ? 's' : ''}</span>
                </div>
              )}
              <button onClick={load} className="btn-secondary py-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="px-6 py-4 flex gap-4 bg-white border-b border-gray-200 flex-shrink-0">
          {[
            { label: 'Total Drivers',     value: fleet.length,                               color: 'text-blue-600' },
            { label: 'Available to Drive', value: fleet.filter(d => parseInt(d.driving_minutes_remaining) > 60).length, color: 'text-green-600' },
            { label: 'Near Limit (<1h)',   value: fleet.filter(d => parseInt(d.driving_minutes_remaining) <= 60).length, color: 'text-red-600' },
            { label: 'Violations Today',   value: alerts.length,                              color: 'text-orange-600' },
          ].map(s => (
            <div key={s.label} className="flex-1 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-auto px-6 py-4">
          <div className="card overflow-hidden">
            {loading ? <TableSkeleton rows={5} cols={6} /> : fleet.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">No HOS data available</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="table-header">Driver</th>
                    <th className="table-header">Driving Remaining</th>
                    <th className="table-header">Duty Window</th>
                    <th className="table-header">Cycle Remaining</th>
                    <th className="table-header">Break In</th>
                    <th className="table-header">Violations</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {fleet.map(d => {
                    const viol = (() => { try { return typeof d.violations === 'string' ? JSON.parse(d.violations) : (d.violations || []); } catch { return []; } })();
                    return (
                      <tr key={d.driver_id} className="hover:bg-blue-50/30 cursor-pointer transition-colors" onClick={() => setSelected(d)}>
                        <td className="table-cell">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {d.driver_name?.split(' ').map(n => n[0]).join('').slice(0,2)}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{d.driver_name}</div>
                              <div className="text-xs text-gray-500">{d.current_hos_cycle?.replace(/_/g,' ')} · CDL {d.cdl_class}</div>
                            </div>
                          </div>
                        </td>
                        <td className={`table-cell rounded`}>
                          <div className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${hosColor(d.driving_minutes_remaining)}`}>
                            {fmtMin(d.driving_minutes_remaining)}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${hosColor(d.duty_window_minutes_remaining)}`}>
                            {fmtMin(d.duty_window_minutes_remaining)}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${hosColor(d.cycle_minutes_remaining)}`}>
                            {fmtMin(d.cycle_minutes_remaining)}
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className={`text-sm font-medium ${parseInt(d.minutes_until_break_required) < 60 ? 'text-red-600' : 'text-gray-700'}`}>
                            {fmtMin(d.minutes_until_break_required)}
                          </span>
                        </td>
                        <td className="table-cell">
                          {viol.length > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-bold">
                              ⚠ {viol.length}
                            </span>
                          ) : (
                            <span className="text-green-600 font-medium text-xs">✓ Clear</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Driver detail panel */}
      {selected && (
        <DriverHOSPanel summary={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
